import React from "react";
import { Header, Footer } from "./App";
import { Bi } from "./lang";
import Seo from "./Seo";

export default function AboutPage() {
  return (
    <>
      <Seo
        title="Über JUMIBI – KI-Prozessautomatisierung aus der Praxis | JUMIBI"
        description="JUMIBI baut KI-Prozessautomatisierung aus der Praxis von Finance und SAP – nicht aus dem Labor. Erfahren Sie, wer hinter der Plattform steht."
      />
      <Header />
      <main>
        {/* PAGE HERO */}
        <section className="page-hero" id="AboutHero">
          <div className="container">
            <span className="eyebrow eyebrow--dark"><Bi en="About JUMIBI" de="Über JUMIBI" /></span>
            <h1 className="h1">
              <Bi en="From the industrial machine room — not the AI lab." de="Aus dem industriellen Maschinenraum — nicht aus dem KI-Labor." />
            </h1>
            <p>
              <Bi
                en="We know the difference between an accountant's day and a slide about “digital transformation”. That grounding shows up in every line of what we build."
                de="Wir kennen den Unterschied zwischen dem Arbeitstag eines Buchhalters und einer Folie über „digitale Transformation“. Diese Erdung steckt in jeder Zeile dessen, was wir bauen."
              />
            </p>
          </div>
        </section>

        {/* PROMISE */}
        <section className="section">
          <div className="container">
            <div className="split">
              <div>
                <span className="eyebrow"><Bi en="The promise" de="Das Versprechen" /></span>
                <h2 className="h2" style={{ marginTop: 14 }}>Existing systems, new capabilities.</h2>
                <p className="lede" style={{ marginTop: 18 }}>
                  <Bi
                    en="Measurable operational improvement through productized AI — inside your ERP stack, not in spite of it. No revolution, no rebuild, no migration. We add a process layer on top of what already runs."
                    de="Messbare operative Verbesserung durch produktisierte KI — in Ihrem ERP-Bestand, nicht trotz ihm. Keine Revolution, kein Umbau, keine Migration. Wir legen eine Prozess-Schicht über das, was bereits läuft."
                  />
                </p>
              </div>
              <div className="kpi-row" style={{ gridTemplateColumns: "repeat(2,1fr)" }}>
                <div className="kpi"><div className="v">5</div><div className="l"><Bi en="Live modules" de="Live-Module" /></div></div>
                <div className="kpi"><div className="v">2–4<span className="u" style={{ fontSize: 24 }}> wks</span></div><div className="l"><Bi en="Per-site roll-out" de="Roll-out / Standort" /></div></div>
                <div className="kpi"><div className="v">€55<span className="u">M</span></div><div className="l"><Bi en="Invoice volume / 30-day cycle" de="Rechnungsvolumen / 30 Tage" /></div></div>
                <div className="kpi"><div className="v">1–3<span className="u"> FTE</span></div><div className="l"><Bi en="Freed per site" de="Freigesetzt / Standort" /></div></div>
              </div>
            </div>
          </div>
        </section>

        {/* PERSONALITY ANCHORS */}
        <section className="section section--ice">
          <div className="container">
            <div className="section-head">
              <span className="eyebrow"><Bi en="How we show up" de="Wie wir auftreten" /></span>
              <h2 className="h2" style={{ marginTop: 14 }}>
                <Bi en="Three anchors carry every conversation" de="Drei Anker tragen jede Konversation" />
              </h2>
            </div>
            <div className="grid grid-3">
              <div className="card">
                <div className="top-rule" />
                <h3 className="h3"><Bi en="Operator mentality" de="Operator-Mentalität" /></h3>
                <p>
                  <Bi
                    en="We come from the industrial machine room. Validated in production across an industrial PE portfolio with multiple entities."
                    de="Wir kommen aus dem industriellen Maschinenraum. Validiert im Produktivbetrieb über ein industrielles PE-Portfolio mit mehreren Entities."
                  />
                </p>
              </div>
              <div className="card">
                <div className="top-rule" />
                <h3 className="h3"><Bi en="Pragmatism over hype" de="Pragmatismus statt Hype" /></h3>
                <p>
                  <Bi
                    en="AI is a means, not an end. We talk about 80% touchless invoice capture, four-week roll-outs and two FTE freed per site — not “autonomous agents”."
                    de="KI ist Mittel, nicht Selbstzweck. Wir reden über 80% automatisierte Rechnungserfassung, vierwöchige Roll-outs und zwei FTE pro Standort — nicht über „autonome Agenten“."
                  />
                </p>
              </div>
              <div className="card">
                <div className="top-rule" />
                <h3 className="h3"><Bi en="Reliability" de="Verlässlichkeit" /></h3>
                <p>
                  <Bi
                    en="Enterprise-grade, not startup tinkering. Secure, plannable, documented, auditable, compliant. When in doubt, boring beats exciting."
                    de="Enterprise-grade, nicht Startup-Bastelei. Sicher, planbar, dokumentiert, auditierbar, compliant. Im Zweifel schlägt langweilig aufregend."
                  />
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* DIFFERENTIATORS */}
        <section className="section">
          <div className="container">
            <div className="section-head">
              <span className="eyebrow"><Bi en="Differentiation" de="Differenzierung" /></span>
              <h2 className="h2" style={{ marginTop: 14 }}>
                <Bi en="What no other player combines" de="Was kein anderer Player kombiniert" />
              </h2>
            </div>
            <div className="grid grid-3">
              <div className="card"><h3 className="h3" style={{ fontSize: 19 }}><Bi en="AI-native, not OCR" de="KI-nativ statt OCR" /></h3><p><Bi en="Extraction without templates. Learns from examples; no per-supplier setup like tangro." de="Extraktion ohne Templates. Lernt aus Beispielen; keine Lieferanten-Konfiguration wie bei tangro." /></p></div>
              <div className="card"><h3 className="h3" style={{ fontSize: 19 }}><Bi en="ERP-agnostic" de="ERP-agnostisch" /></h3><p><Bi en="SAP, ProAlpha, Microsoft Dynamics, Oracle, Infor and more, connected in parallel." de="SAP, ProAlpha, Microsoft Dynamics, Oracle, Infor und weitere — parallel angebunden." /></p></div>
              <div className="card"><h3 className="h3" style={{ fontSize: 19 }}><Bi en="Portfolio visibility built in" de="Portfolio-Sichtbarkeit eingebaut" /></h3><p><Bi en="Cross-entity dashboard, process mining and cost-per-invoice benchmarking — native, not bolted on." de="Cross-Entity-Dashboard, Process Mining und Cost-per-Invoice-Benchmarking — nativ, nicht aufgesetzt." /></p></div>
              <div className="card"><h3 className="h3" style={{ fontSize: 19 }}><Bi en="Multi-country compliance" de="Multi-Country-Compliance" /></h3><p><Bi en="Italian SDI, French PPF/PDP, Serbian E-Faktura live as a JUMIBI module — more countries without specialists." de="Italienisches SDI, französisches PPF/PDP, serbisches E-Faktura live als JUMIBI-Modul — weitere Länder ohne Spezialanbieter." /></p></div>
              <div className="card"><h3 className="h3" style={{ fontSize: 19 }}><Bi en="Own SAP team" de="Eigenes SAP-Team" /></h3><p><Bi en="Internal SAP developers and consultants. Configurations and integrations in-house — not via external partners." de="Interne SAP-Entwickler und Consultants. Konfigurationen und Integrationen in-house — nicht über externe Partner." /></p></div>
              <div className="card"><h3 className="h3" style={{ fontSize: 19 }}><Bi en="PE-grade speed" de="PE-taugliche Geschwindigkeit" /></h3><p><Bi en="Eight to twelve weeks per site — productized for PE hold periods, not eight quarters." de="Acht bis zwölf Wochen pro Standort — produktisiert für PE-Halteperioden, nicht acht Quartale." /></p></div>
            </div>
          </div>
        </section>

        {/* NAME ORIGIN */}
        <section className="section section--ice">
          <div className="container">
            <div className="split">
              <div>
                <span className="eyebrow"><Bi en="The name" de="Der Name" /></span>
                <h2 className="h2" style={{ marginTop: 14 }}>
                  <Bi en="Why we're called JUMIBI" de="Warum wir JUMIBI heißen" />
                </h2>
                <p className="lede" style={{ marginTop: 18 }}>
                  <Bi
                    en="JUMIBI is built from junbi (準備) — Japanese for readiness: the deliberate preparation before something serious begins. That is the whole job. Standard SaaS hands you a tool and leaves you to it. We make the process itself ready for AI — analysed, built to fit your operation, wired into your existing ERP landscape — so it runs in production, not in a pilot. We don't ship software off the shelf. We ship readiness."
                    de="JUMIBI ist aus junbi (準備) gebaut — japanisch für Bereitschaft: die bewusste Vorbereitung, bevor etwas Ernstes beginnt. Genau das ist die Aufgabe. Standard-SaaS gibt Ihnen ein Werkzeug und lässt Sie allein. Wir machen den Prozess selbst AI-ready — analysiert, passgenau für Ihren Betrieb gebaut, in Ihre bestehende ERP-Landschaft eingebunden — damit er im Produktivbetrieb läuft, nicht im Pilot. Wir liefern keine Software von der Stange. Wir liefern Bereitschaft."
                  />
                </p>
                <p style={{ marginTop: 18 }}>
                  <Bi
                    en="The same first syllable carries a second meaning: a junction — the point where every entity's processes meet one layer and become AI-ready together. One name, both ideas: prepared, and connected."
                    de="Die gleiche erste Silbe trägt eine zweite Bedeutung: eine Junction — der Knotenpunkt, an dem die Prozesse jeder Entity auf eine Schicht treffen und gemeinsam AI-ready werden. Ein Name, beide Ideen: vorbereitet und verbunden."
                  />
                </p>
              </div>
              <div className="card" style={{ alignSelf: "center" }}>
                <div className="top-rule" />
                <div style={{ fontSize: 64, lineHeight: 1, letterSpacing: 4 }} lang="ja" aria-hidden="true">準備</div>
                <p style={{ marginTop: 16, fontSize: 18, fontWeight: 600 }}>
                  junbi <span style={{ fontWeight: 400, opacity: 0.6 }}>· /ˈdʒʊn.bi/ · Japanese</span>
                </p>
                <p style={{ marginTop: 4 }}>
                  <Bi en="noun — readiness; the preparation before action." de="Substantiv — Bereitschaft; die Vorbereitung vor dem Handeln." />
                </p>
                <div className="top-rule" style={{ margin: "20px 0" }} />
                <p style={{ fontSize: 14, opacity: 0.7 }}>
                  <Bi
                    en="JUMIBI keeps a letter more — our own wordmark, inspired by the word, not borrowed from it."
                    de="JUMIBI trägt einen Buchstaben mehr — eine eigene Wortmarke, inspiriert vom Wort, nicht von ihm geliehen."
                  />
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* TRANSITION */}
        <section className="section section--steel">
          <div className="container">
            <div className="split">
              <div>
                <span className="eyebrow eyebrow--dark"><Bi en="One brand" de="Eine Marke" /></span>
                <h2 className="h2" style={{ marginTop: 14, color: "#fff" }}>
                  <Bi en="From DocInsight to JUMIBI AP" de="Von DocInsight zu JUMIBI AP" />
                </h2>
                <p className="on-dark-2" style={{ marginTop: 18, fontSize: 17, lineHeight: 1.7 }}>
                  <Bi
                    en="DocInsight is our most established product name and carries real equity with active customers. We consolidate it under the JUMIBI roof without disrupting them — DocInsight becomes JUMIBI AP."
                    de="DocInsight ist unsere etablierteste Produktbezeichnung und trägt echte Equity bei aktiven Kunden. Wir konsolidieren sie unter dem JUMIBI-Dach, ohne diese zu stören — DocInsight wird zu JUMIBI AP."
                  />
                </p>
              </div>
              <div className="rename">
                <div className="rename__pair">
                  <span className="rename__from">DocInsight</span>
                  <span className="rename__arrow" aria-hidden="true">→</span>
                  <span className="rename__to">JUMIBI AP</span>
                </div>
                <p className="rename__note">
                  <Bi
                    en="Same product, same team — new name. Existing DocInsight customers keep everything; only the label moves under the JUMIBI roof."
                    de="Gleiches Produkt, gleiches Team — neuer Name. Bestandskunden von DocInsight behalten alles; nur die Bezeichnung wandert unter das JUMIBI-Dach."
                  />
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="section--sm" style={{ padding: "96px 0" }}>
          <div className="container">
            <div className="cta-panel">
              <h2 className="h2"><Bi en="Talk to an operator, not a sales deck." de="Sprechen Sie mit einem Operator, nicht mit einem Sales-Deck." /></h2>
              <p>
                <Bi
                  en="30-minute demo of the live system, with the numbers. Roll-out per site: 2–4 weeks over your ERP API."
                  de="30-Minuten-Demo des Live-Systems, mit den Zahlen. Roll-out pro Standort: 2–4 Wochen über Ihre ERP-API."
                />
              </p>
              <div className="hero-cta">
                <a href="/#book" className="btn btn--primary"><Bi en="Pick a slot" de="Termin wählen" /></a>
                <a href="/#book" className="btn btn--outline"><Bi en="See available dates →" de="Verfügbare Termine ansehen →" /></a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
